/**
 * AuthSessionProvider
 *
 * Listens for authentication session expiration events and redirects
 * the user to the login page with the current location preserved as
 * the return URL. Also follows the account across tabs: see
 * `useAuthStorageSync`.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { AuthSessionProviderProps } from './AuthSessionProvider.types';

import { useAuthStorageSync } from '@/hooks/auth';
import { authEvents } from '@/services/auth-events';
import { useAuthStore } from '@/stores';
import { PUBLIC_ROUTES } from '@/utils';

export function AuthSessionProvider({
  children,
}: AuthSessionProviderProps): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const isRedirectingRef = useRef(false);

  // A sign-out or sign-in in another tab reaches this one through storage.
  useAuthStorageSync();

  const handleSessionExpired = useCallback(() => {
    // Debounce: prevent multiple redirects from rapid 401s
    if (isRedirectingRef.current) {
      return;
    }
    isRedirectingRef.current = true;

    // Navigate to login with current location preserved, stamped with whose
    // it was. The event fires before the handler clears the store, so the
    // user is still there to read.
    navigate(PUBLIC_ROUTES.LOGIN, {
      state: {
        from: location,
        fromUser: useAuthStore.getState().user?.username ?? null,
      },
      replace: true,
    });

    // Reset after navigation completes
    setTimeout(() => {
      isRedirectingRef.current = false;
    }, 1000);
  }, [navigate, location]);

  useEffect(() => {
    // Subscribe to session expired events
    const unsubscribe = authEvents.on(
      'auth:session-expired',
      handleSessionExpired
    );
    return unsubscribe;
  }, [handleSessionExpired]);

  return <>{children}</>;
}
